import { useState, useCallback, useRef } from "react";
import {
  listDocumentReferencesForPatient,
  NO_PROVIDER_NOTES_MESSAGE,
  retrieveNotesByMetadata,
  type DecodedNoteResult,
  type DocumentListResult,
  type NoteRetrievalProgress,
  type NoteRetrievalStep,
  type RetrievedNoteMetadata,
} from "../services/vaNotesService";
import { getCachedVaNotes, retrieveVaNotesOnServer } from "../services/vaNotesCache";

export interface UseFetchRecentNoteParams {
  patientIcn?: string;
  preferredSta3n?: string;
  patientName?: string;
  doctorName?: string;
  patientId?: number | null;
}

export interface UseFetchRecentNoteReturn {
  fetchNoteList: (overrideParams?: UseFetchRecentNoteParams) => Promise<RetrievedNoteMetadata[]>;
  fetchNotesByMetadata: (
    metadatas: RetrievedNoteMetadata[]
  ) => Promise<DecodedNoteResult[] | null>;
  fetchLastSignedNote: () => Promise<DecodedNoteResult | null>;
  isLoading: boolean;
  step: NoteRetrievalStep;
  stepMessage: string;
  error: string | null;
  documents: RetrievedNoteMetadata[];
  lastProviderNote: RetrievedNoteMetadata | null;
  cachedNotes: RetrievedNoteMetadata[];
  noteResults: DecodedNoteResult[];
  noteResult: DecodedNoteResult | null;
  dismissError: () => void;
  reset: () => void;
}

export function useFetchRecentNote(
  initialParams?: UseFetchRecentNoteParams
): UseFetchRecentNoteReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<NoteRetrievalStep>("idle");
  const [stepMessage, setStepMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<RetrievedNoteMetadata[]>([]);
  const [lastProviderNote, setLastProviderNote] = useState<RetrievedNoteMetadata | null>(null);
  const [cachedNotes, setCachedNotes] = useState<RetrievedNoteMetadata[]>([]);
  const [noteResults, setNoteResults] = useState<DecodedNoteResult[]>([]);
  const requestIdRef = useRef(0);
  const listResultRef = useRef<DocumentListResult | null>(null);

  const resolveParams = useCallback(
    (overrideParams?: UseFetchRecentNoteParams) => {
      const rawPatientId = overrideParams?.patientId ?? initialParams?.patientId ?? null;
      const parsedPatientId = Number(rawPatientId);
      return {
        patientIcn: (overrideParams?.patientIcn ?? initialParams?.patientIcn ?? "").trim(),
        preferredSta3n: overrideParams?.preferredSta3n || initialParams?.preferredSta3n,
        patientId:
          Number.isInteger(parsedPatientId) && parsedPatientId > 0 ? parsedPatientId : null,
      };
    },
    [initialParams?.patientIcn, initialParams?.patientId, initialParams?.preferredSta3n]
  );

  const applyProgress = useCallback((currentRequestId: number, progress: NoteRetrievalProgress) => {
    if (currentRequestId === requestIdRef.current) {
      setStep(progress.step);
      setStepMessage(progress.message);
    }
  }, []);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    listResultRef.current = null;
    setIsLoading(false);
    setStep("idle");
    setStepMessage("");
    setError(null);
    setDocuments([]);
    setLastProviderNote(null);
    setCachedNotes([]);
    setNoteResults([]);
  }, []);

  const dismissError = useCallback(() => {
    setError(null);
    setStep(listResultRef.current ? "complete" : "idle");
    setStepMessage(listResultRef.current ? "Notes found." : "");
  }, []);

  const fetchNoteList = useCallback(
    async (overrideParams?: UseFetchRecentNoteParams): Promise<RetrievedNoteMetadata[]> => {
      const { patientIcn, preferredSta3n, patientId } = resolveParams(overrideParams);

      if (!patientIcn) {
        setError("Patient ICN is required to retrieve clinical notes.");
        setStep("idle");
        setStepMessage("");
        setIsLoading(false);
        return [];
      }

      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      setError(null);
      setNoteResults([]);
      setStep("connecting");
      setStepMessage("Connecting to VA...");

      try {
        const shouldReadCache = typeof patientId === "number" && patientId > 0;
        const [result, cached] = await Promise.all([
          listDocumentReferencesForPatient({
            patientIcn,
            preferredSta3n,
            onProgress: (progress) => applyProgress(currentRequestId, progress),
          }),
          shouldReadCache
            ? getCachedVaNotes(patientId).catch(() => [])
            : Promise.resolve([]),
        ]);

        if (currentRequestId !== requestIdRef.current) {
          return [];
        }

        listResultRef.current = result;
        setDocuments(result.metadatas);
        setLastProviderNote(result.lastProviderNote);
        setCachedNotes(
          cached.map((note) => ({
            id: note.id,
            binaryId: note.binaryId,
            binaryUrl: "",
            date: note.date,
            title: note.title,
          }))
        );

        setStep("complete");
        setStepMessage("Notes found.");
        return result.metadatas;
      } catch (err: unknown) {
        if (currentRequestId === requestIdRef.current) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to retrieve clinical notes from VA.";
          setError(errorMessage);
          setStep("idle");
          setStepMessage("");
          setDocuments([]);
          setLastProviderNote(null);
          setCachedNotes([]);
          listResultRef.current = null;
        }
        return [];
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [applyProgress, resolveParams]
  );

  const fetchNotesByMetadata = useCallback(
    async (metadatas: RetrievedNoteMetadata[]): Promise<DecodedNoteResult[] | null> => {
      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      setError(null);
      setNoteResults([]);
      setStep("retrieving");
      setStepMessage("Retrieving note content...");

      try {
        const list = listResultRef.current;
        if (!list) {
          throw new Error("Clinical notes have not been loaded yet.");
        }

        const { patientId } = resolveParams();
        const results =
          typeof patientId === "number" && patientId > 0
            ? await retrieveVaNotesOnServer(patientId, metadatas)
            : await retrieveNotesByMetadata({
                metadatas,
                providerContext: list.providerContext,
                accessToken: list.accessToken,
                onProgress: (progress) => applyProgress(currentRequestId, progress),
              });

        if (currentRequestId !== requestIdRef.current) {
          return null;
        }

        if (typeof patientId === "number" && patientId > 0) {
          setCachedNotes(metadatas);
        }

        setNoteResults(results);
        setStep("complete");
        setStepMessage("Note retrieved successfully.");
        return results;
      } catch (err: unknown) {
        if (currentRequestId === requestIdRef.current) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to retrieve clinical note from VA.";
          setError(errorMessage);
          setStep("idle");
          setStepMessage("");
        }
        return null;
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [applyProgress, resolveParams]
  );

  const fetchLastSignedNote = useCallback(async (): Promise<DecodedNoteResult | null> => {
    if (!listResultRef.current) {
      const metadatas = await fetchNoteList();
      if (!metadatas.length) {
        return null;
      }
    }

    const latest = listResultRef.current?.lastProviderNote ?? null;
    if (!latest) {
      setError(NO_PROVIDER_NOTES_MESSAGE);
      setStep("idle");
      setStepMessage("");
      setIsLoading(false);
      return null;
    }

    const results = await fetchNotesByMetadata([latest]);
    return results?.[0] ?? null;
  }, [fetchNoteList, fetchNotesByMetadata]);

  return {
    fetchNoteList,
    fetchNotesByMetadata,
    fetchLastSignedNote,
    isLoading,
    step,
    stepMessage,
    error,
    documents,
    lastProviderNote,
    cachedNotes,
    noteResults,
    noteResult: noteResults[0] ?? null,
    dismissError,
    reset,
  };
}

export default useFetchRecentNote;
